import React from 'react';
import { SearchFieldMetadataDTO, FieldType } from '../../../types';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';

interface AdvancedSearchFieldProps {
    field: SearchFieldMetadataDTO;
    value: any;
    onChange: (value: any) => void;
    onClear: () => void;
}

const AdvancedSearchField: React.FC<AdvancedSearchFieldProps> = ({
    field,
    value,
    onChange,
    onClear
}) => {
    const hasValue = value !== undefined && value !== null && value !== '';

    const renderInput = () => {
        switch (field.fieldType) {
            case FieldType.TEXT:
                return (
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder || `Enter ${field.displayLabel.toLowerCase()}`}
                        className="text-input"
                    />
                );

            case FieldType.NUMBER:
                return (
                    <input
                        type="number"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder={field.placeholder || `Enter ${field.displayLabel.toLowerCase()}`}
                        className="number-input"
                    />
                );

            case FieldType.DROPDOWN:
                return (
                    <select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value || undefined)}
                        className="dropdown-input"
                    >
                        <option value="">Select {field.displayLabel}</option>
                        {field.dropdownOptions?.map(option => (
                            <option key={option} value={option}>
                                {field.fieldName === 'accountType' ?
                                    (option === 'business' ? 'Business' : 'Individual') :
                                    option}
                            </option>
                        ))}
                    </select>
                );

            case FieldType.DATE_RANGE:
                // For date range, we'll handle dateCreated specially
                if (field.fieldName === 'dateCreated') {
                    const dateValue = value || {};
                    return (
                        <div className="date-range-container">
                            <input
                                type="date"
                                value={dateValue.start || ''}
                                onChange={(e) => {
                                    const newValue = { ...dateValue, start: e.target.value };
                                    // Pass the separate values to parent for backend mapping
                                    onChange(newValue);
                                }}
                                className="date-input"
                                placeholder="Start date"
                            />
                            <span className="date-separator">to</span>
                            <input
                                type="date"
                                value={dateValue.end || ''}
                                onChange={(e) => {
                                    const newValue = { ...dateValue, end: e.target.value };
                                    // Pass the separate values to parent for backend mapping
                                    onChange(newValue);
                                }}
                                className="date-input"
                                placeholder="End date"
                            />
                        </div>
                    );
                }
                return null;

            default:
                return (
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder || `Enter ${field.displayLabel.toLowerCase()}`}
                        className="text-input"
                    />
                );
        }
    };

    return (
        <div className="advanced-search-field">
            <label className="field-label">
                {field.displayLabel}
                {field.required && <span className="required">*</span>}
            </label>
            <div className="field-input-container">
                {renderInput()}
                {hasValue && (
                    <button
                        type="button"
                        className="clear-field-btn"
                        onClick={onClear}
                        aria-label={`Clear ${field.displayLabel}`}
                    >
                        <Icon path={mdiClose} size={0.6} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default AdvancedSearchField;